import React from 'react';

function TitleSection() {
  return (
    <section className="relative overflow-hidden bg-white">
      {/* Decorative background */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            'radial-gradient(60% 60% at 50% 0%, rgba(99,102,241,0.18) 0%, rgba(99,102,241,0) 60%), radial-gradient(40% 40% at 100% 30%, rgba(236,72,153,0.10) 0%, rgba(236,72,153,0) 60%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        aria-hidden="true"
        style={{
          backgroundImage:
            'linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          WebkitMaskImage:
            'radial-gradient(ellipse at top, black 30%, transparent 70%)',
          maskImage:
            'radial-gradient(ellipse at top, black 30%, transparent 70%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-20 text-center">
        {/* Venue badge */}
        <div className="flex justify-center mb-6">
          <a
            href="https://iclr.cc/Conferences/2026"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/80 backdrop-blur px-4 py-1.5 text-xs font-semibold text-brand-700 shadow-soft hover:border-brand-300 hover:bg-white transition"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse" />
            Accepted at ICLR 2026
          </a>
        </div>

        {/* Title */}
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight text-ink-900">
          <span
            style={{
              backgroundImage:
                'linear-gradient(135deg, #15161d 0%, #4338ca 55%, #db2777 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            Superficial Safety
          </span>
          <br />
          <span className="text-ink-900">Alignment Hypothesis</span>
        </h1>

        {/* Subtitle */}
        <p className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-ink-500 leading-relaxed">
          Safety alignment teaches an unsafe model the correct{' '}
          <em className="text-brand-700 not-italic font-semibold">reasoning direction</em> with a
          simple refusal mechanism &mdash; not a deep behavioral rewrite.
        </p>

        {/* Authors */}
        <div className="mt-10">
          <p className="text-base md:text-lg font-medium text-ink-800">
            <a href="https://jianwei-li.com" className="hover:text-brand-700 transition-colors">
              Jianwei Li
            </a>
            <span className="mx-2 text-ink-300">&middot;</span>
            <a
              href="https://www.junguen.com"
              className="hover:text-brand-700 transition-colors"
            >
              Jung-Eun Kim
              <sup className="text-ink-400 ml-0.5">*</sup>
            </a>
          </p>
          <p className="mt-2 text-sm text-ink-500">
            Department of Computer Science, North Carolina State University
          </p>
          <p className="mt-1 text-sm text-ink-400 font-mono">
            {`{jli265, jung-eun.kim}@ncsu.edu`}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a
            href="https://arxiv.org/abs/2410.10862"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-semibold text-white shadow-card hover:bg-ink-700 hover:-translate-y-0.5 transition-all"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 4h7v5h5v11H6V4zm2 9v2h8v-2H8zm0 4v2h5v-2H8z" />
            </svg>
            Paper (arXiv)
          </a>
          <a
            href="https://github.com/JEKimLab/SSAH"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-ink-200 px-5 py-2.5 text-sm font-semibold text-ink-800 shadow-soft hover:border-ink-300 hover:-translate-y-0.5 transition-all"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0022 12.017C22 6.484 17.522 2 12 2z"
              />
            </svg>
            Code
            <span className="ml-1 inline-flex items-center rounded-md bg-ink-100 px-1.5 py-0.5 text-[10px] font-semibold text-ink-700">
              JEKimLab/SSAH
            </span>
          </a>
          <a
            href="https://arxiv.org/pdf/2410.10862"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-ink-200 px-5 py-2.5 text-sm font-semibold text-ink-800 shadow-soft hover:border-ink-300 hover:-translate-y-0.5 transition-all"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
            </svg>
            PDF
          </a>
          <a
            href="#citation"
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-ink-200 px-5 py-2.5 text-sm font-semibold text-ink-800 shadow-soft hover:border-ink-300 hover:-translate-y-0.5 transition-all"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h6m-6 4h10M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
            </svg>
            BibTeX
          </a>
        </div>

        {/* Tagline numbers */}
        <div className="mt-14 grid grid-cols-3 max-w-2xl mx-auto gap-4">
          <div className="card px-3 py-5">
            <div className="text-3xl font-bold text-brand-700 font-serif">7.5%</div>
            <div className="text-xs text-ink-500 mt-1">params frozen for safety</div>
          </div>
          <div className="card px-3 py-5">
            <div className="text-3xl font-bold text-brand-700 font-serif">20%</div>
            <div className="text-xs text-ink-500 mt-1">redundant alignment budget</div>
          </div>
          <div className="card px-3 py-5">
            <div className="text-3xl font-bold text-brand-700 font-serif">~1.3%</div>
            <div className="text-xs text-ink-500 mt-1">exclusive safety units</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TitleSection;
